/**
 * Stale-build recovery.
 *
 * Port of `zeedog-tanstack/src/sdk/staleBuild.ts`. Every deploy publishes
 * assets under a new content hash and removes the previous build's — any
 * tab that was already open keeps pointing at the old hashes, and the next
 * chunk it needs (a lazy section, a new route, a modal) fails to load. The
 * error bubbles to the route's `errorComponent` and the user sees an error
 * screen on a navigation that has nothing actually wrong with it.
 *
 * Only a reload fixes it: the new HTML ships the new hashes.
 * `router.invalidate()` doesn't help — it reruns the loader, but the import
 * still points at a file that no longer exists.
 */

const RELOAD_KEY = "miess:stale-build-reload";
const RELOAD_COOLDOWN_MS = 30_000;

const STALE_BUILD_PATTERNS = [
  // Chrome/Safari/Firefox phrasing for the same case: import() of a chunk
  // that's gone.
  /dynamically imported module/i,
  /importing a module script failed/i,
  /error loading dynamically imported module/i,
  /ChunkLoadError/i,
  /Loading (CSS )?chunk .* failed/i,
  // A server function's id is derived from the build; an old client POSTing
  // to a new server lands here.
  /invalid server function/i,
];

export function isStaleBuildError(error: unknown): boolean {
  const message = error instanceof Error
    ? `${error.name}: ${error.message}`
    : String(error ?? "");
  return STALE_BUILD_PATTERNS.some((pattern) => pattern.test(message));
}

/**
 * Reload the page once when the error looks like a stale build. Returns
 * `true` if it triggered the reload (the caller doesn't need to render
 * anything).
 *
 * The `sessionStorage` cooldown is what prevents a loop: if reloading
 * didn't fix it (a different error, or the deploy really is broken), the
 * second occurrence within 30s falls through to the normal error screen
 * instead of reloading forever. `sessionStorage` can throw (private mode,
 * blocked storage), so the read/write is best-effort — without it the
 * behavior degrades to "never reloads", never to "reloads in a loop".
 */
export function recoverFromStaleBuild(error: unknown): boolean {
  if (typeof window === "undefined" || !isStaleBuildError(error)) return false;

  try {
    const last = Number(sessionStorage.getItem(RELOAD_KEY) ?? 0);
    if (Date.now() - last < RELOAD_COOLDOWN_MS) return false;
    sessionStorage.setItem(RELOAD_KEY, String(Date.now()));
  } catch {
    return false;
  }

  window.location.reload();
  return true;
}
